import Button from 'components/Button'
import Drawer from 'components/Drawer'
import {
  deleteRoundAssessmentMapping,
  getRoundAssessmentMapping,
  listCorporateAssessments,
  upsertRoundAssessmentMapping,
} from 'modules/Drives/actions'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
`

const MappingStrip = styled.div`
  background: #f6f9ff;
  border: 1px solid #d6e2ff;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MappingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  outline: none;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #eef0f4;
  border-radius: 6px;
  max-height: 56vh;
  overflow-y: auto;
`

const Row = styled.label`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f1f5;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  background: ${p => (p.selected ? '#eef4ff' : 'white')};
  opacity: ${p => (p.disabled ? 0.55 : 1)};
  &:last-child {
    border-bottom: none;
  }
`

const TypeChip = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.bg || '#eef0f4'};
  color: ${p => p.color || '#465065'};
  white-space: nowrap;
`

const Meta = styled.div`
  font-size: 12px;
  color: #6f7888;
`

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #6f7888;
  font-size: 13px;
`

const ErrorBanner = styled.div`
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 6px;
  padding: 10px 12px;
  color: #a8071a;
  font-size: 13px;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`

const typeChipStyle = typeName => {
  const t = (typeName || '').toLowerCase()
  if (t.includes('aptitude')) return { bg: '#e6f0ff', color: '#274fb3' }
  if (t.includes('behavi')) return { bg: '#efeaff', color: '#5a3fb3' }
  if (t.includes('role')) return { bg: '#e7f7ee', color: '#1f7a3a' }
  if (t.includes('comm')) return { bg: '#fff1e0', color: '#a55a0d' }
  if (t.includes('custom')) return { bg: '#ececec', color: '#444' }
  return { bg: '#eef0f4', color: '#465065' }
}

// Two ids are considered same-set if their string forms match. Defensive
// because admin-node stringifies UUIDs and we compare client-side.
const sameId = (a, b) => String(a) === String(b)

const DrawerContent = ({
  loading,
  fetchError,
  assessments,
  mappings,
  search,
  setSearch,
  selectedIds,
  toggleSelected,
  onUnmapOne,
  onSave,
  onCancel,
  saving,
  dirty,
}) => {
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase()
    if (!q) return assessments
    return assessments.filter(a => (a.name || '').toLowerCase().includes(q))
  }, [search, assessments])

  return (
    <Section>
      {fetchError && (
        <ErrorBanner>
          Couldn’t load the current mappings. Close and try again — saving is disabled until we know what’s mapped.
        </ErrorBanner>
      )}

      {mappings.length > 0 ? (
        <MappingStrip>
          <div style={{ fontWeight: 600 }}>Currently mapped ({mappings.length}):</div>
          {mappings.map(m => (
            <MappingRow key={m.assessment_corporate_map_id}>
              <div>
                <div>
                  {m.assessment_name || m.assessment_corporate_map_id}
                </div>
                {m.type_name && <Meta>Type: {m.type_name}</Meta>}
              </div>
              <Button.Danger
                text="Unmap"
                onClick={() => onUnmapOne(m.assessment_corporate_map_id)}
              />
            </MappingRow>
          ))}
        </MappingStrip>
      ) : (
        <Meta>No assessments are currently mapped to this round.</Meta>
      )}

      <SearchInput
        placeholder="Search corporate assessments…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <List>
        {loading ? (
          <Empty>Loading assessments…</Empty>
        ) : filtered.length === 0 ? (
          <Empty>No assessments found for this corporate.</Empty>
        ) : (
          filtered.map(a => {
            const isSelected = selectedIds.some(id => sameId(id, a.id))
            const chipStyle = typeChipStyle(a.type_name)
            return (
              <Row key={a.id} selected={isSelected}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelected(a.id)}
                />
                <div>
                  <div style={{ fontWeight: 500 }}>{a.name}</div>
                  <Meta>{a.assigned_candidate_count ?? 0} assigned</Meta>
                </div>
                <TypeChip bg={chipStyle.bg} color={chipStyle.color}>
                  {a.type_name || 'Unknown'}
                </TypeChip>
              </Row>
            )
          })
        )}
      </List>

      <Footer>
        <Button.Secondary text="Cancel" onClick={onCancel} />
        <Button.Primary
          text={saving ? 'Saving…' : 'Save Mapping'}
          onClick={onSave}
          disabled={saving || fetchError || !dirty}
        />
      </Footer>
    </Section>
  )
}

const SelectAssessmentDrawer = ({
  driveId,
  roleId,
  round,
  corporateId,
  open,
  onClose,
  onMappingChange,
}) => {
  const dispatch = useDispatch()
  const [assessments, setAssessments] = useState([])
  const [mappings, setMappings] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [originalIds, setOriginalIds] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    let active = true
    if (!open) return
    setLoading(true)
    setFetchError(false)
    ;(async () => {
      const [listResult, mappingResult] = await Promise.all([
        dispatch(listCorporateAssessments({ corporateId })),
        dispatch(getRoundAssessmentMapping({ driveId, roleId, round })),
      ])
      if (!active) return
      const listOk = listResult?.ok !== false
      const mappingOk = mappingResult?.ok !== false
      const items = Array.isArray(listResult?.items) ? listResult.items : []
      const mapped = Array.isArray(mappingResult?.mappings) ? mappingResult.mappings : []
      const mappedIds = mapped.map(m => m.assessment_corporate_map_id).filter(Boolean)
      setAssessments(items)
      setMappings(mapped)
      setSelectedIds(mappedIds)
      setOriginalIds(mappedIds)
      setFetchError(!listOk || !mappingOk)
      setLoading(false)
    })()
    return () => {
      active = false
    }
  }, [open, dispatch, corporateId, driveId, roleId, round])

  const toggleSelected = id => {
    setSelectedIds(prev => {
      if (prev.some(x => sameId(x, id))) {
        return prev.filter(x => !sameId(x, id))
      }
      return [...prev, id]
    })
  }

  const dirty = useMemo(() => {
    if (selectedIds.length !== originalIds.length) return true
    const a = [...selectedIds].sort()
    const b = [...originalIds].sort()
    return a.some((v, i) => v !== b[i])
  }, [selectedIds, originalIds])

  const handleSave = async () => {
    if (fetchError || !dirty) return
    setSaving(true)
    const result = await dispatch(
      upsertRoundAssessmentMapping({
        driveId,
        roleId,
        round,
        assessmentCorporateMapIds: selectedIds,
      })
    )
    setSaving(false)
    if (result?.ok === false) return
    if (typeof onMappingChange === 'function') onMappingChange()
    onClose()
  }

  const handleUnmapOne = async assessmentCorporateMapId => {
    const target = mappings.find(
      m => sameId(m.assessment_corporate_map_id, assessmentCorporateMapId)
    )
    if (
      !window.confirm(
        `Unmap "${target?.assessment_name || 'this assessment'}" from "${round}"?`
      )
    ) {
      return
    }
    const result = await dispatch(
      deleteRoundAssessmentMapping({
        driveId,
        roleId,
        round,
        assessmentCorporateMapId,
      })
    )
    if (result?.ok === false) return
    setMappings(prev =>
      prev.filter(m => !sameId(m.assessment_corporate_map_id, assessmentCorporateMapId))
    )
    setSelectedIds(prev => prev.filter(id => !sameId(id, assessmentCorporateMapId)))
    setOriginalIds(prev => prev.filter(id => !sameId(id, assessmentCorporateMapId)))
    if (typeof onMappingChange === 'function') onMappingChange()
  }

  return (
    <Drawer
      title={`Select Assessments for "${round}"`}
      width="42%"
      placement="right"
      visible={open}
      content={
        <DrawerContent
          loading={loading}
          fetchError={fetchError}
          assessments={assessments}
          mappings={mappings}
          search={search}
          setSearch={setSearch}
          selectedIds={selectedIds}
          toggleSelected={toggleSelected}
          onUnmapOne={handleUnmapOne}
          onSave={handleSave}
          onCancel={onClose}
          saving={saving}
          dirty={dirty}
        />
      }
      onClose={onClose}
      closable
    />
  )
}

export default SelectAssessmentDrawer
